import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { QzTrayConnectionTestResult, QzTrayStatus } from "../../../types/qztray/QzTrayConnectionTypes";

/**
 * 🌐 Estado global del monitor QZ Tray Connection
 *
 * Controla toda la información del estado actual del cliente QZ Tray:
 * - Estado (connected, disconnected, error)
 * - Versión del cliente
 * - Endpoint dinámico
 * - Latencia (responseTimeMs)
 * - Hora del último chequeo
 * - Resultado del último ping o test
 */
interface QzTrayConnectionState {
  status: QzTrayStatus | null;
  testResult: QzTrayConnectionTestResult | null;
  isPingLoading: boolean;
  isReconnectLoading: boolean;
  isVersionLoading: boolean;
  isRefreshing: boolean;
  error: string | null;
}

const initialState: QzTrayConnectionState = {
  status: null,
  testResult: null,
  isPingLoading: false,
  isReconnectLoading: false,
  isVersionLoading: false,
  isRefreshing: false,
  error: null,
};

const qzTrayConnectionSlice = createSlice({
  name: "qzTrayConnection",
  initialState,
  reducers: {
    // 📊 Actualizar estado general
    setStatus(state, action: PayloadAction<QzTrayStatus | null>) {
      state.status = action.payload;
      state.error = null;
    },

    // 🧪 Resultado de prueba puntual (ping)
    setTestResult(state, action: PayloadAction<QzTrayConnectionTestResult | null>) {
      state.testResult = action.payload;
    },

    // ⚙️ Indicadores individuales
    setPingLoading(state, action: PayloadAction<boolean>) {
      state.isPingLoading = action.payload;
    },
    setReconnectLoading(state, action: PayloadAction<boolean>) {
      state.isReconnectLoading = action.payload;
    },
    setVersionLoading(state, action: PayloadAction<boolean>) {
      state.isVersionLoading = action.payload;
    },
    setRefreshing(state, action: PayloadAction<boolean>) {
      state.isRefreshing = action.payload;
    },

    // ❌ Errores
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },

    // 🔄 Reiniciar todo el monitor
    resetQzTrayConnectionState(state) {
      Object.assign(state, initialState);
    },
  },
});

export const {
  setStatus,
  setTestResult,
  setPingLoading,
  setReconnectLoading,
  setVersionLoading,
  setRefreshing,
  setError,
  resetQzTrayConnectionState,
} = qzTrayConnectionSlice.actions;

export default qzTrayConnectionSlice.reducer;
